import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import '../../styles.css';
import './TaskBoard.css';
import { Session } from '../utils/session';
import { API_BASE_URL, SHOW_TEAMS_FILTER } from '../../config';
import KanbanHeaderCell from './KanbanHeaderCell';
import KanbanCardsColumn from './KanbanCardsColumn';
import TaskCardModal from '../Tasks/TaskCardModal';
import Sidebar from '../Sidebar';
import SearchableUserIdSelect from '../SearchableUserIdSelect';
import PageLoader, { LoaderButtonLabel } from '../PageLoader';

const PAGE_SIZE = 5000;

function getDefaultDates() {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - 15);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { dateFrom: fmt(from), dateTo: fmt(today) };
}

function buildPageNumbers(current, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) pages.push('...');
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (end < totalPages - 1) pages.push('...');
  pages.push(totalPages);
  return pages;
}

function normalizeWorkspace(w) {
  return {
    id: String(w.WorkspaceID ?? w.workspaceID ?? w.workspaceId ?? w.id ?? ''),
    name: w.WorkspaceName ?? w.workspaceName ?? w.name ?? '',
  };
}

function compareBoardCards(a, b) {
  const posA = Number(a.position ?? a.Position ?? Number.MAX_SAFE_INTEGER);
  const posB = Number(b.position ?? b.Position ?? Number.MAX_SAFE_INTEGER);
  if (posA !== posB) return posA - posB;

  const tA = new Date(a.createdAt ?? a.CreatedAt ?? 0).getTime();
  const tB = new Date(b.createdAt ?? b.CreatedAt ?? 0).getTime();
  return tB - tA;
}

function normalizeBoard(b) {
  return {
    boardID: b.BoardID ?? b.boardID ?? b.id,
    workspaceID: String(b.WorkspaceID ?? b.workspaceID ?? b.workspaceId ?? ''),
    boardName: b.BoardName ?? b.boardName ?? b.name ?? '',
  };
}

function normalizeTaskUser(u) {
  return {
    userId: u.userId ?? u.UserId,
    userName: u.userName ?? u.UserName ?? '',
    firstName: u.firstName ?? u.FirstName ?? '',
    lastName: u.lastName ?? u.LastName ?? '',
  };
}

export default function TaskBoardContent() {
  const token = Session.getToken();
  const isMember = Session.isMember();
  const currentUserId = Session.getUserId() ? String(Session.getUserId()) : '';

  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [allBoards, setAllBoards] = useState([]);
  const [boardColumns, setBoardColumns] = useState([]);
  const [cards, setCards] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  const defaultDates = useMemo(() => getDefaultDates(), []);

  const [users, setUsers] = useState([]);
  const [userInput, setUserInput] = useState(() => (isMember ? currentUserId : ''));
  const [searchInput, setSearchInput] = useState('');
  const [dateFromInput, setDateFromInput] = useState(defaultDates.dateFrom);
  const [dateToInput, setDateToInput] = useState(defaultDates.dateTo);

  const [selectedUserId, setSelectedUserId] = useState(() => (isMember ? currentUserId : ''));
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState(defaultDates.dateFrom);
  const [dateTo, setDateTo] = useState(defaultDates.dateTo);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardBoardName, setSelectedCardBoardName] = useState('');
  const [priorities, setPriorities] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);

  const toggleSidebar = () => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar(s => !s);
    } else {
      setHideDesktop(s => !s);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const cardsByBoard = useMemo(() => {
    const map = {};
    (cards || []).forEach((card) => {
      const key = card.boardID;
      if (!map[key]) map[key] = [];
      map[key].push(card);
    });
    Object.keys(map).forEach((key) => {
      map[key].sort(compareBoardCards);
    });
    return map;
  }, [cards]);

  const workspaceBoards = useMemo(() => {
    if (!selectedWorkspaceId) return allBoards;
    return allBoards.filter((b) => b.workspaceID === selectedWorkspaceId);
  }, [allBoards, selectedWorkspaceId]);

  const displayBoardColumns = useMemo(() => {
    const columns = selectedWorkspaceId
      ? workspaceBoards.map((board) => ({
        boardID: board.boardID,
        boardName: board.boardName,
      }))
      : boardColumns.map((col) => ({
        boardID: col.boardID,
        boardName: col.boardName,
      }));

    return columns.map((board) => ({
      ...board,
      cardCount: cardsByBoard[board.boardID]?.length || 0,
    }));
  }, [selectedWorkspaceId, workspaceBoards, boardColumns, cardsByBoard]);

  useEffect(() => {
    if (!token) return;

    // Load workspaces (Teams dropdown)
    fetch(`${API_BASE_URL}/api/Workspace/Workspaces_List`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const list = (data || []).map(normalizeWorkspace).filter((w) => w.id);
        setWorkspaces(list);
      })
      .catch((err) => setError(err.message || 'Failed to load workspaces'));

    fetch(`${API_BASE_URL}/api/TaskBoard/boards`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = (data || []).map(normalizeBoard).filter((b) => b.boardID);
        setAllBoards(list);
      })
      .catch(() => setAllBoards([]));

    fetch(`${API_BASE_URL}/api/Members/Users_List`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => (res.ok ? res.json() : []))
      .then((data) => setUsers((data || []).map(normalizeTaskUser).filter((u) => u.userId)))
      .catch(() => setUsers([]));

    fetch(`${API_BASE_URL}/api/Cards/card-priorities`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => (res.ok ? res.json() : []))
      .then((data) => setPriorities(data || []))
      .catch(() => setPriorities([]));
  }, [token]);

  const loadBoardData = useCallback(async (silent = false) => {
    if (!token) return;

    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(PAGE_SIZE),
    });
    if (selectedWorkspaceId) params.append('workspaceId', selectedWorkspaceId);
    const effectiveUserId = isMember ? currentUserId : selectedUserId;
    if (effectiveUserId) params.append('assignedUserId', effectiveUserId);
    if (searchText.trim()) params.append('searchText', searchText.trim());
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    try {
      const res = await fetch(`${API_BASE_URL}/api/TaskBoard/data?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const errBody = await res.json();
          message = errBody.detail || errBody.message || message;
        } catch {
          // ignore JSON parse errors
        }
        throw new Error(message);
      }
      const data = await res.json();

      setBoardColumns(data.boardColumns || []);
      setCards(data.cardsPage?.cards || []);
      setTotalCount(data.cardsPage?.totalCount || 0);
    } catch (err) {
      setError(err.message || 'Failed to load tasks');
      setBoardColumns([]);
      setCards([]);
      setTotalCount(0);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [token, selectedWorkspaceId, selectedUserId, searchText, dateFrom, dateTo, pageNumber, isMember, currentUserId, refreshKey]);

  useEffect(() => {
    loadBoardData();
  }, [loadBoardData]);

  const handleSearch = () => {
    setSelectedUserId(userInput);
    setSearchText(searchInput.trim());
    setDateFrom(dateFromInput);
    setDateTo(dateToInput);
    setPageNumber(1);
    setRefreshKey((k) => k + 1);
  };

  const handleClearFilter = () => {
    const fresh = getDefaultDates();
    const userDefault = isMember ? currentUserId : '';
    setUserInput(userDefault);
    setSearchInput('');
    setDateFromInput(fresh.dateFrom);
    setDateToInput(fresh.dateTo);
    setSelectedUserId(userDefault);
    setSearchText('');
    setDateFrom(fresh.dateFrom);
    setDateTo(fresh.dateTo);
    setPageNumber(1);
    setRefreshKey((k) => k + 1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleAddCard = async (boardId, cardTitle) => {
    const res = await fetch(`${API_BASE_URL}/api/TaskBoard/cards`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        boardID: Number(boardId),
        cardTitle,
        assignedUserID: isMember
          ? Number(currentUserId) || null
          : (selectedUserId ? Number(selectedUserId) : null),
      }),
    });
    if (!res.ok) {
      let message = `Failed to add card (HTTP ${res.status})`;
      try {
        const errBody = await res.json();
        message = errBody.detail || errBody.message || message;
      } catch {
        // ignore JSON parse errors
      }
      throw new Error(message);
    }
    const data = await res.json();
    await loadBoardData(true);
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const cardId = Number(draggableId);
    const sourceBoardId = Number(source.droppableId);
    const destBoardId = Number(destination.droppableId);

    const prevCards = cards;
    const prevColumns = boardColumns;

    const sourceCards = [...(cardsByBoard[sourceBoardId] || [])];
    const destCards =
      sourceBoardId === destBoardId
        ? sourceCards
        : [...(cardsByBoard[destBoardId] || [])];
    const [moved] = sourceCards.splice(source.index, 1);
    if (!moved) return;
    destCards.splice(destination.index, 0, { ...moved, boardID: destBoardId });

    const merged = [];
    const keys = new Set([
      ...Object.keys(cardsByBoard).map(Number),
      sourceBoardId,
      destBoardId,
    ]);
    [...keys].sort((a, b) => a - b).forEach((key) => {
      if (key === sourceBoardId && key === destBoardId) merged.push(...sourceCards);
      else if (key === sourceBoardId) merged.push(...sourceCards);
      else if (key === destBoardId) merged.push(...destCards);
      else merged.push(...(cardsByBoard[key] || []));
    });
    setCards(merged);

    setBoardColumns((cols) =>
      cols.map((c) => {
        if (sourceBoardId === destBoardId) return c;
        if (c.boardID === sourceBoardId) {
          return { ...c, cardCount: Math.max(0, c.cardCount - 1) };
        }
        if (c.boardID === destBoardId) {
          return { ...c, cardCount: c.cardCount + 1 };
        }
        return c;
      })
    );

    try {
      const res = await fetch(`${API_BASE_URL}/api/TaskBoard/move-card`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cardID: cardId,
          boardID: destBoardId,
          position: destination.index + 1,
        }),
      });
      if (!res.ok) throw new Error('Move failed');
    } catch {
      setCards(prevCards);
      setBoardColumns(prevColumns);
      setError('Failed to move card. Please try again.');
      await loadBoardData(true);
    }
  };

  const handleCardClick = (card) => {
    const board =
      displayBoardColumns.find((b) => b.boardID === card.boardID) ||
      boardColumns.find((b) => b.boardID === card.boardID);
    setSelectedCardBoardName(board?.boardName || card.boardName || '');
    setSelectedCard(card);
  };

  const rangeStart = totalCount === 0 ? 0 : (pageNumber - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(pageNumber * PAGE_SIZE, totalCount);
  const pageNumbers = buildPageNumbers(pageNumber, totalPages);
  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile'); else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop'); else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width'); else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  return (
    <>
      <div id="sidebar-overlay" className="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} />

      <Sidebar />
      <div className="tasks-page-outer">
        <div id='contenttwo'>
        <div className="tasks-page-header mb-3">
            <div className="d-flex align-items-center mb-3 ">
              <button
                type="button"
                className="btn btn-toggle me-3"
                onClick={toggleSidebar}
              >
                <i className="fas fa-bars" />
              </button>
            </div>
            <div className="tasks-header-row">
              <h1 className="tasks-title">Tasks</h1>
              {SHOW_TEAMS_FILTER && (
                <div className="tasks-team-inline">
                  <span className="tasks-team-label">Teams:</span>
                  <select
                    id="team-board-select"
                    className="form-select tasks-team-select-inline"
                    value={selectedWorkspaceId}
                    onChange={(e) => {
                      setSelectedWorkspaceId(e.target.value);
                      setPageNumber(1);
                    }}
                  >
                    <option value="">All</option>
                    {workspaces.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

       
          </div>
        </div>
   
        <div
          className="main-card margin-left-mobile tasks-page-shell" id="content"
          style={{
            margin: '0 auto',
            marginLeft: sidebarOpen ? '283px' : '20px',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none',
          }}
        >
             <div className="tasks-filters">
              {!isMember && (
                <SearchableUserIdSelect
                  className="tasks-filter-user"
                  users={users}
                  value={userInput}
                  onChange={setUserInput}
                  allLabel="All Users"
                  searchPlaceholder="Search user..."
                />
              )}

              <input
                type="text"
                className="form-control tasks-search-input"
                placeholder="Search task"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />

              <input
                type="date"
                className="form-control tasks-date-input"
                title="Created from"
                aria-label="Created from"
                value={dateFromInput}
                onChange={(e) => setDateFromInput(e.target.value)}
              />
              <input
                type="date"
                className="form-control tasks-date-input"
                title="Created to"
                aria-label="Created to"
                value={dateToInput}
                onChange={(e) => setDateToInput(e.target.value)}
              />

              <button type="button" className="btn btn-primary btn-sm" onClick={handleSearch} disabled={loading || refreshing}>
                <LoaderButtonLabel loading={loading || refreshing} loadingLabel="Searching...">
                  Search
                </LoaderButtonLabel>
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleClearFilter} disabled={loading || refreshing}>
                <LoaderButtonLabel loading={loading || refreshing} loadingLabel="Clearing...">
                  Clear Filter
                </LoaderButtonLabel>
              </button>

              <div className="tasks-pagination">
                <span className="tasks-showing-text">
                  Showing {rangeStart}-{rangeEnd} of {totalCount}
                </span>
                <button
                  type="button"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                >
                  &lsaquo;
                </button>
                {pageNumbers.map((p, idx) =>
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`}>...</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={pageNumber === p ? 'active' : ''}
                      onClick={() => setPageNumber(p)}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  disabled={pageNumber >= totalPages}
                  onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                >
                  &rsaquo;
                </button>
              </div>
            </div>

            {error && <div className="alert alert-danger mb-0">{error}</div>}

          <div className="tasks-page-body epa-page-loader-host">
            {loading && <PageLoader label="Loading tasks..." overlay />}
            {refreshing && !loading && <PageLoader label="Updating tasks..." compact overlay />}

            {!loading && displayBoardColumns.length > 0 && (
              <div className={`kanban-board-viewport${refreshing ? ' epa-content-dimmed' : ''}`}>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <div className="kanban-board kanban-board-split">
                    <div className="kanban-head-row">
                      {displayBoardColumns.map((board) => (
                        <KanbanHeaderCell
                          key={board.boardID}
                          board={board}
                          cardCount={board.cardCount ?? (cardsByBoard[board.boardID] || []).length}
                          onAddCard={handleAddCard}
                        />
                      ))}
                    </div>
                    <div className="kanban-cards-scroll">
                      <div className="kanban-cards-row">
                        {displayBoardColumns.map((board) => (
                          <KanbanCardsColumn
                            key={board.boardID}
                            board={board}
                            cards={cardsByBoard[board.boardID] || []}
                            onCardClick={handleCardClick}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </DragDropContext>
              </div>
            )}

            {!loading && displayBoardColumns.length === 0 && (
              <div className="tasks-loading">No boards available.</div>
            )}
          </div>
        </div>

        {selectedCard && (
          <TaskCardModal
            card={selectedCard}
            statusName={selectedCardBoardName}
            users={users}
            priorities={priorities}
            onClose={() => setSelectedCard(null)}
            onSaved={() => loadBoardData(true)}
            onDeleted={() => loadBoardData(true)}
          />
        )}
      </div>
    </>
  );
}
